// Shown instead of the work-zone panel to an employee with Remote Punch on:
// no zone to be inside, just "you may punch from anywhere", the name of the
// area the phone thinks it is in right now (no map — the admin has that),
// how accurate that is, and why the buttons are locked when they are.
import React, { useEffect, useState } from "react";
import { MapPinned, MapPin, RefreshCw, Smartphone, AlertTriangle, Crosshair } from "lucide-react";
import { accuracyQuality } from "../../../components/RemotePunch/PunchMap";
import { areaNameFor, remotePunchApi } from "../../../components/RemotePunch/remotePunchApi";
import { rpText } from "../../../components/RemotePunch/remotePunchText";

/**
 * The area name for a position. Looked up again only when the phone moves
 * about 100 m (3 decimals), so GPS jitter doesn't call the server.
 */
const useAreaName = (lat, lng) => {
  const [state, setState] = useState({ loading: false, area: null });
  const key = Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(3)},${lng.toFixed(3)}` : null;

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    const [la, ln] = key.split(",").map(Number);
    setState((s) => ({ ...s, loading: true }));
    remotePunchApi.myArea(la, ln)
      .then((area) => { if (!cancelled) setState({ loading: false, area: area || null }); })
      .catch(() => { if (!cancelled) setState({ loading: false, area: null }); });
    return () => { cancelled = true; };
  }, [key]);

  return state;
};

const RemoteLocationPanel = ({ remotePunch, locationState, refreshLocation, deviceBlocked, accuracyBlocked, currentTheme, isDarkMode, language }) => {
  const t = rpText(language);
  const c = currentTheme.colors;
  const loc = locationState?.userLocation;
  const accuracy = Number.isFinite(loc?.accuracy) ? Math.round(loc.accuracy) : null;
  const quality = accuracyQuality(accuracy);
  const limit = remotePunch.accuracyThresholdMeters;
  const isLoading = locationState.isLoadingUserLocation;
  const warnings = [];
  if (deviceBlocked) warnings.push({ Icon: Smartphone, text: t.deviceBlocked });
  if (accuracyBlocked && accuracy != null && limit) warnings.push({ Icon: AlertTriangle, text: t.accuracyLow(accuracy, limit) });

  const lat = Number.isFinite(loc?.lat) ? loc.lat : null;
  const lng = Number.isFinite(loc?.lng) ? loc.lng : null;
  const { loading: areaLoading, area } = useAreaName(lat, lng);
  const areaName = areaNameFor(area, language);

  return (
    <div style={{
      background: isDarkMode ? "linear-gradient(135deg,rgba(30,41,59,0.9),rgba(15,23,42,0.95))" : "linear-gradient(135deg,#f8fafc,#f1f5f9)",
      borderRadius: "20px", padding: "18px", marginTop: "16px",
      border: `1.5px solid ${isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(203,213,225,0.8)"}`,
      fontFamily: "'Cairo', sans-serif",
      textAlign: "start",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${c.primary}1a`, color: c.primary }}>
          <MapPinned size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "15px", fontWeight: 800, color: c.textPrimary }}>
            {t.badge}
            {remotePunch.remote?.endDate ? <span style={{ fontWeight: 600, color: c.textSecondary }}> · {t.until} {remotePunch.remote.endDate}</span> : null}
          </div>
          <div style={{ fontSize: "12.5px", color: c.textSecondary, marginTop: 2 }}>{t.badgeHint}</div>
        </div>
      </div>

      {lat != null && lng != null && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px", marginBottom: "12px", borderRadius: "12px", background: isDarkMode ? "rgba(255,255,255,0.04)" : "#ffffff", border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(203,213,225,0.8)"}` }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${c.primary}1a`, color: c.primary }}>
            <MapPin size={18} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "12px", fontWeight: 700, color: c.textSecondary }}>{t.currentLocation}</div>
            {areaName ? (
              <div style={{ fontSize: "15px", fontWeight: 800, color: c.textPrimary, marginTop: 2 }}>{areaName}</div>
            ) : (
              <div style={{ fontSize: "13px", color: c.textSecondary, marginTop: 2 }}>
                {areaLoading ? t.locatingArea : t.areaUnknown}
              </div>
            )}
          </div>
        </div>
      )}

      {accuracy != null && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 10px", padding: "10px 12px", marginBottom: "12px", borderRadius: "12px", background: isDarkMode ? "rgba(255,255,255,0.04)" : "#ffffff", border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(203,213,225,0.8)"}` }}>
          <Crosshair size={16} color={quality.color} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: "13px", color: c.textSecondary, whiteSpace: "nowrap" }}>{t.yourAccuracy}</span>
          <span style={{ fontSize: "15px", fontWeight: 800, color: c.textPrimary, whiteSpace: "nowrap" }}>
            <span dir="ltr">± {accuracy}</span> {language === "ar" ? "م" : "m"}
          </span>
          <span style={{ marginInlineStart: "auto", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", color: quality.color, background: `${quality.color}1f` }}>
            {t.quality[quality.level]}
          </span>
        </div>
      )}

      {warnings.map(({ Icon, text }) => (
        <div key={text} style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "10px 14px", marginBottom: "10px", borderRadius: "12px", background: `${c.danger}12`, border: `1px solid ${c.danger}35`, color: c.danger, fontSize: "13px", fontWeight: 600 }}>
          <Icon size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{text}</span>
        </div>
      ))}

      <button onClick={refreshLocation} disabled={isLoading}
        style={{ width: "100%", background: isDarkMode ? `${c.primary}20` : `${c.primary}10`, border: `1.5px solid ${c.primary}35`, borderRadius: "12px", padding: "11px 20px", color: c.primary, fontSize: "14px", fontWeight: 700, fontFamily: "'Cairo', sans-serif", cursor: isLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", opacity: isLoading ? 0.6 : 1 }}
      >
        <RefreshCw size={16} style={isLoading ? { animation: "spin 0.8s linear infinite" } : undefined} />
        {t.refreshLocation}
      </button>
    </div>
  );
};

export default RemoteLocationPanel;
